import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { RaasMetricsData, RaasMetricsResult } from 'pages/raas/hooks';
import { useMemo } from 'react';
import TRow from 'shared/components/Table/TRow';
import { ColumnConfig } from 'shared/types/table';

interface IPeriodicCostTable {
  periodicMetricData: RaasMetricsResult<RaasMetricsData>;
}

type MetricRow = {
  name: string;
  color: string;
  value: number;
};

const PeriodicCostTable: React.FC<IPeriodicCostTable> = ({ periodicMetricData }) => {
  const formattedData: MetricRow[] = useMemo(() => {
    return periodicMetricData?.dataKeys.map(dataKey => {
      const latestData = periodicMetricData.data[periodicMetricData.data.length - 1];

      return {
        name: dataKey.name,
        color: dataKey.color ?? '#ccc',
        value: Number(latestData[dataKey.name] ?? 0),
      };
    });
  }, [periodicMetricData]);

  const columnConfig: ColumnConfig<MetricRow>[] = useMemo(
    () => [
      {
        id: 'name',
        Header: 'Label',
        Cell: row => (
          <Stack alignItems="center" direction="row" spacing={1}>
            <Box
              borderRadius="20px"
              height="10px"
              minWidth="20px"
              sx={{ backgroundColor: row.color }}
              width="20px"
            />
            <Typography variant="caption">{row.name}</Typography>
          </Stack>
        ),
      },
      {
        id: 'value',
        Header: 'Last Data',
        Cell: row => (
          <Typography textAlign="right" variant="caption">
            {row.value.toFixed(2)}
          </Typography>
        ),
        align: 'right',
      },
    ],
    [],
  );

  return (
    <TableContainer sx={{ maxHeight: '390px', overflowY: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            {columnConfig.map(col => (
              <TableCell align={col.align || 'left'} key={col.id}>
                {col.Header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {formattedData.map(row => (
            <TRow<MetricRow> columnConfig={columnConfig} key={row.name} rowData={row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PeriodicCostTable;
